/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-use-before-define */
/* eslint-disable react/jsx-no-bind */
import Icon, { ArrowDownOutlined, ArrowUpOutlined, UpOutlined } from "@ant-design/icons";
import { Badge, Button, Tabs, Tooltip, message } from "antd";
import { isEmpty } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import EngagedAI from "../../../../static/EngagedAi_trans_bg.svg";
import TranslateIcon from "../../../../static/icons/TranslateIcon.svg";
import { UserContext } from "../../../amplify/authenticator-provider";
import { SVGIcon } from "../../../library/basicComponents";
import { colorConfig } from "../../../library/colorConfig";
import { AccessPermissionAction } from "../../../library/constants";
import { checkAccessPermission } from "../../../library/helpers";
import { applicationUseCaseMapping, getApplicationUseCaseId } from "../../../library/utils";
import ChatMagic from "./chatMagic";
import ReplyAsSelector from "./ReplyAsSelector";
import "./editor.css";
import { addFilesToAttachments, InputTextAreaWrap } from "./engagementActivity";
import { setTextMode } from "../../../redux/slices/leadSlice";
import { getMobileView } from "../../../redux/slices/isMobile";
import useBrowserExt from "../../../hooks/useBrowserExt";
import { EmailTextBox } from "./EmailTextBox";
import { AI_RESPONSE_COPY } from "./aiResponse/constants";
import CustomizeAiResponse from "./aiResponse/CustomizeAiResponse";
import DraftAiFeedback from "./DraftAiFeedback";

// Re-export the wrapper as the default export
export { InputAreaWrapper } from "./InputAreaWrapper";

// Styled Tabs component that makes tab content panels take full height
const StyledTabs = styled(Tabs)`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-tabs-content-holder {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;

function SuggestAMessageButton({
  createSuggestedResponse,
  misc,
  selectedLead,
  disabled,
  text,
  setinputText,
  setloaderFn,
  selectedOppTypeId,
}) {
  const { user } = useContext(UserContext);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const [customizePromptData, setcustomizePromptData] = useState({
    quickTipIds: [],
    toneIds: [],
    additionalNote: "",
  });

  const resolvedUseCaseId = getApplicationUseCaseId(
    applicationUseCases,
    applicationUseCaseMapping.ENGAGEMENT_MESSAGE_SUGGESTION
  )?.id;
  const locationId =
    Number(String(sessionStorage.getItem("currentLocationId") || "").split(",")[0]) ||
    user?.location?.id ||
    undefined;

  const quickTipVariables = {
    companyId: user.company.id,
    // With an active opp-type tab, defer to BE department derivation via
    // rootOppTypeId; otherwise keep the legacy user.departmentIds path.
    locationIds:
      !selectedOppTypeId && Array.isArray(user?.departmentIds) && user.departmentIds.length > 0
        ? null
        : sessionStorage.getItem("currentLocationId")?.split(","),
    applicationUseCaseId: resolvedUseCaseId,
    departmentIds: selectedOppTypeId ? null : user?.departmentIds || null,
    rootOppTypeId: selectedOppTypeId || null,
  };

  const buildResponsePayload = promptData => ({
    aiSuggestionUserInput: { ...promptData },
    leadId: selectedLead.id,
    companyId: user.company.id,
    applicationUseCaseId: resolvedUseCaseId,
  });

  return (
    <Button
      key={selectedLead?.id}
      loading={misc.loading}
      className="text-lg flex bg-white !border !border-solid !border-[#E2E8F0] hover:border-separate flex-row items-center justify-between p-1 gap-1"
      disabled={disabled}
    >
      <Tooltip title={AI_RESPONSE_COPY.GENERATE_TOOLTIP}>
        <Icon component={EngagedAI} alt="EngagedAi" onClick={() => createSuggestedResponse()} />
      </Tooltip>
      <div className="h-[20px] w-[1px] bg-[#E2E8F0]" />
      <CustomizeAiResponse
        user={user}
        locationId={locationId}
        applicationUseCaseId={resolvedUseCaseId}
        quickTipVariables={quickTipVariables}
        buildResponsePayload={buildResponsePayload}
        text={text}
        setInputText={setinputText}
        setLoader={setloaderFn}
        value={customizePromptData}
        onChange={setcustomizePromptData}
        renderTrigger={({ open }) => (
          <Tooltip title={AI_RESPONSE_COPY.CUSTOMIZE_TOOLTIP}>
            <span className="flex-row-centered items-center cursor-pointer">
              <UpOutlined
                className={`text-[#64748B] text-[14px] ${
                  open ? "rotate-0" : "rotate-180"
                } transition duration-300`}
              />
            </span>
          </Tooltip>
        )}
      />
    </Button>
  );
}

// InputArea is now wrapped by InputAreaWrapper which uses key={selectedLeadId}
// This means the component will ONLY remount when lead changes
// Scroll/message updates will NOT affect this component at all!
//
// PLUS: We wrap with React.memo to prevent re-renders even when props change
// The memo comparison only checks if the TEXT REF changes (which it never does)
export const InputArea = React.memo(
  function InputArea(props) {
    const { textMode, selectedLead } = useSelector(state => state.lead);
    const dispatch = useDispatch();
    const { isBrowserExt } = useBrowserExt();
    const isMobileView = useSelector(state => getMobileView(state));
    const [loaderFn, setloaderFn] = useState(false);
    const canDisabledConversation = checkAccessPermission(
      AccessPermissionAction.LEAD_MANAGEMENT_DISABLED_CONVERSATION
    );

    const canSendEmail = checkAccessPermission(AccessPermissionAction.EMAIL_MANAGEMENT_SEND);

    // Email consent (EAI-1666): an explicit opt-out means the backend rejects the
    // send, so don't offer the composer at all. Only `false` counts -- null is
    // unrecorded consent, which does send, and undefined also covers any flow
    // whose query didn't select the field (the backend still enforces it either way).
    const isEmailOptedOut = selectedLead?.emailConsent === false;

    // Mark textarea with AI generating state so BottomActions can check it
    useEffect(() => {
      if (props?.text?.current) {
        props.text.current.dataset.aiGenerating = loaderFn ? "true" : "false";
      }
    }, [loaderFn]);

    // Since this component remounts on lead change (via key prop),
    // we can safely clear the input on mount
    useEffect(() => {
      if (props?.text?.current) {
        props.text.current.value = "";
        props.text.current.style.height = "0px";
      }
    }, []); // Empty deps - only on mount (which happens on lead change)

    useEffect(() => {
      if (!props?.messageText) {
        props?.setOriginalText?.("");
      }
    }, [props?.messageText, props?.setOriginalText]);

    useEffect(() => {
      // Set default active tab based on phone/email availability
      if (selectedLead) {
        const hasPhone = !isEmpty(selectedLead.phoneNumbers);
        const hasEmail = !isEmpty(selectedLead.emails);

        if (!hasPhone && hasEmail && !isEmailOptedOut) {
          dispatch(setTextMode("Email"));
        } else {
          dispatch(setTextMode("Text"));
        }
      }
    }, []); // Empty deps - only set on mount

    // Email consent (EAI-1666): leave the Email tab if the lead is opted out —
    // including when consent is toggled in the Consents panel next to this
    // composer. antd keeps rendering the active tab's content after it becomes
    // disabled, which would otherwise leave a usable composer and Send button.
    useEffect(() => {
      if (isEmailOptedOut && textMode === "Email") {
        dispatch(setTextMode("Text"));
      }
    }, [isEmailOptedOut, textMode]);

    const componentProps = {
      ...props,
      selectedLead, // Get from Redux, not props!
      setInputMode: mode => dispatch(setTextMode(mode)),
      loaderFn,
      setloaderFn,
      canDisabledConversation,
      disableToEmail: !canSendEmail,
    };

    const hasPhone = !isEmpty(selectedLead?.phoneNumbers);
    const hasEmail = !isEmpty(selectedLead?.emails);

    // Don't recreate tabItems on every render - memoize the children components
    // This prevents Ant Design Tabs from unmounting/remounting the textarea
    const smsTextBox = React.useMemo(() => <SMSTextBox {...componentProps} />, [componentProps]);
    const emailTextBox = React.useMemo(
      () => <EmailTextBox {...componentProps} useFlexLayout />,
      [componentProps]
    );

    const tabItems = [
      {
        label: "Text",
        key: "Text",
        disabled: isEmpty(selectedLead) || !hasPhone,
        children: smsTextBox,
      },
      {
        label: isEmailOptedOut ? (
          <Tooltip title="This customer has opted out of email. Email cannot be sent to this lead.">
            <span>Email</span>
          </Tooltip>
        ) : (
          "Email"
        ),
        key: "Email",
        disabled: isEmpty(selectedLead) || !hasEmail || !canSendEmail || isEmailOptedOut,
        children: emailTextBox,
      },
    ];

    return (
      <div className="flex flex-row justify-center h-full relative">
        <StyledTabs
          activeKey={textMode}
          onChange={key => dispatch(setTextMode(key))}
          className="w-full"
          items={tabItems}
          size="small"
          tabBarExtraContent={
            <div className="flex items-center gap-2">
              {/* Reply-as control (EAI-1530) — managers only. Default rule +
                  per-lead override; resolution/persistence owned by the parent. */}
              {props.isReplyAsManager && (
                <ReplyAsSelector
                  currentSigner={props.replyAsSigner}
                  roleOptions={props.replyAsRoleOptions}
                  selectedRole={props.replyAsSelectedRole}
                  roleLoading={props.replyAsRoleLoading}
                  leadRows={props.replyAsLeadRows}
                  hasOverride={props.replyAsHasOverride}
                  onSelectRole={props.onReplyAsSelectRole}
                  onSelectUser={props.onReplyAsSelectUser}
                  onClear={props.onReplyAsClear}
                  lang={props.lang}
                  disabled={isEmpty(selectedLead) || props.messageLoader}
                />
              )}
              {!isBrowserExt && isMobileView ? (
                <Tooltip title={props.hideBottom ? "Hide" : "Show"}>
                  <Badge
                    count={!props.hideBottom ? props.messageFilters?.campaignIds?.length : 0}
                    style={{ backgroundColor: "red" }}
                  >
                    <span
                      className="w-6 h-6 rounded-full bg-twLinkColor text-white flex items-center justify-center cursor-pointer mr-2"
                      onClick={() => props.setHideBottom(!props.hideBottom)}
                    >
                      {props.hideBottom ? (
                        <ArrowDownOutlined className="text-xs" />
                      ) : (
                        <ArrowUpOutlined className="text-xs" />
                      )}
                    </span>
                  </Badge>
                </Tooltip>
              ) : null}
            </div>
          }
        />
        {textMode === "Text" && (
          <div className="flex flex-col absolute right-3 bottom-3 items-end gap-1 z-20">
            {/* EAI-1762: rate the AI draft before sending — sits directly above
                the generate/customize button, same thumbs + drawer as a sent
                message. Renders itself null unless an AI draft is live. */}
            <DraftAiFeedback
              selectedLead={selectedLead}
              user={componentProps.user}
              messageText={componentProps.messageText}
              text={componentProps.text}
              selectedOppTypeId={componentProps.selectedOppTypeId}
            />
            <div className="flex items-center gap-1 justify-center">
              {componentProps.isTranslatePreviewShow && (
                <Tooltip
                  title={componentProps.originalText ? "Back to original text" : "Translate text"}
                >
                  <span
                    onClick={() => {
                      if (!(componentProps.text.current?.value.length > 0)) {
                        message.info("Please enter message to translate.");
                        return;
                      }
                      if (componentProps.text.current?.value && !componentProps.originalText) {
                        componentProps.setOriginalText(componentProps.text.current.value);
                        componentProps.translateMessage({
                          variables: {
                            content: componentProps.text.current?.value,
                            targetLanguage: selectedLead?.preferredLanguageCode,
                          },
                          onCompleted: res => {
                            if (res?.translateMessage?.statusCode === 200) {
                              const translatedText = res?.translateMessage?.translatedContent;
                              componentProps.text.current.value = translatedText;
                              componentProps.text.current.style.height = `${componentProps.text.current.scrollHeight}px`;
                              componentProps.setmessageText(translatedText);
                              message.info(res?.translateMessage?.message);
                            } else {
                              message.info(res?.translateMessage?.message);
                            }
                          },
                        });
                        return;
                      }
                      componentProps.text.current.value = componentProps.originalText;
                      componentProps.text.current.style.height = `${componentProps.text.current.scrollHeight}px`;
                      componentProps.setmessageText(componentProps.originalText);
                      componentProps.setOriginalText("");
                    }}
                  >
                    <SVGIcon component={TranslateIcon} alt="Close" />
                  </span>
                </Tooltip>
              )}
              <ChatMagic
                text={componentProps.text}
                richTextValue={componentProps.richTextValue}
                setRichTextValue={componentProps.setrichTextValue}
                selectedLead={selectedLead}
                user={componentProps.user}
                placeholder={componentProps.placeholder}
                setmessageText={componentProps.setmessageText}
                displaySize="small"
                loaderFn={loaderFn}
                setloaderFn={setloaderFn}
                code="ENGAGEMENT_MESSAGE_SUGGESTION"
                selectedOppTypeId={componentProps.selectedOppTypeId}
                messageText={componentProps.messageText}
                Component={({ createSuggestedResponse, misc }) => (
                  <SuggestAMessageButton
                    createSuggestedResponse={createSuggestedResponse}
                    misc={misc}
                    disabled={
                      componentProps.textDisabled() ||
                      !selectedLead?.id ||
                      componentProps.messageLoader ||
                      componentProps.loading ||
                      componentProps.spinner ||
                      componentProps.leadsLoading
                    }
                    text={componentProps.text}
                    loaderFn={loaderFn}
                    setloaderFn={setloaderFn}
                    selectedLead={selectedLead}
                    setinputText={componentProps.setmessageText}
                    selectedOppTypeId={componentProps.selectedOppTypeId}
                  />
                )}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // CRITICAL: This comparison determines when to re-render
    // Return true = block re-render (props are "equal")
    // Return false = allow re-render (props changed)

    // IMPORTANT: We get selectedLead from Redux inside the component,
    // so we should IGNORE props.selectedLead changes completely!
    // The wrapper's key handles lead changes, so we only care about
    // the ID for safety check, not the object reference.

    // Safety check: If lead ID changed, allow re-render
    // (though wrapper's key should handle remounting already)
    const leadIdChanged = prevProps.selectedLead?.id !== nextProps.selectedLead?.id;
    if (leadIdChanged) {
      return false; // Allow re-render
    }

    // Lead ID is same - now check if any CRITICAL props changed
    // We explicitly DO NOT compare selectedLead object itself!
    // When messages load on scroll, selectedLead object changes but ID stays same
    const propsAreEqual =
      prevProps.text === nextProps.text &&
      prevProps.messageLoader === nextProps.messageLoader &&
      prevProps.loading === nextProps.loading &&
      prevProps.spinner === nextProps.spinner &&
      prevProps.leadsLoading === nextProps.leadsLoading &&
      prevProps.textDisabled === nextProps.textDisabled &&
      prevProps.messageText === nextProps.messageText &&
      prevProps.placeholder === nextProps.placeholder &&
      prevProps.buttonDisabled === nextProps.buttonDisabled &&
      prevProps.isEmojiOpen === nextProps.isEmojiOpen &&
      // Reply-as selector (EAI-1530): re-render when the selected sender or
      // the (memoized) candidate list changes so the footer stays in sync.
      prevProps.isReplyAsManager === nextProps.isReplyAsManager &&
      prevProps.replyAsSigner === nextProps.replyAsSigner &&
      prevProps.replyAsSelectedRole === nextProps.replyAsSelectedRole &&
      prevProps.replyAsLeadRows === nextProps.replyAsLeadRows &&
      prevProps.replyAsRoleOptions === nextProps.replyAsRoleOptions &&
      prevProps.replyAsHasOverride === nextProps.replyAsHasOverride &&
      prevProps.replyAsOverrideUserId === nextProps.replyAsOverrideUserId &&
      prevProps.setmessageText === nextProps.setmessageText;

    // Return true to block re-render if all critical props are equal
    return propsAreEqual;
  }
);
const SMSTextBox = React.memo(
  function SMSTextBox({
    buttonStyle,
    text,
    selectedLead,
    handleKeyDown,
    handleKeyUp,
    canDisabledConversation,
    textDisabled,
    messageLoader,
    loading,
    spinner,
    leadsLoading,
    loaderFn,
    lang,
    setisReview,
    placeholder,
    setmessageText,
    setAttachments,
  }) {
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const [mediaProcessing, setMediaProcessing] = useState(false);

    const isUploadDisabled =
      canDisabledConversation ||
      textDisabled() ||
      !selectedLead?.id ||
      messageLoader ||
      loading ||
      spinner ||
      leadsLoading ||
      loaderFn;

    const eventHasFiles = e => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      // DOMStringList lacks Array methods in some browsers — index manually.
      for (let i = 0; i < types.length; i++) {
        if (types[i] === "Files") return true;
      }
      return false;
    };

    const handleDragEnter = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Hint to the OS that this is a copy/upload target.
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleDragLeave = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        addFilesToAttachments(files, setAttachments, setMediaProcessing);
      }
    };

    const handlePaste = e => {
      if (isUploadDisabled || !setAttachments) return;
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length === 0) return;
      // Suppress the textarea's default paste so a pasted image's filename /
      // binary string doesn't end up as message text.
      e.preventDefault();
      addFilesToAttachments(files, setAttachments, setMediaProcessing);
    };

    return (
      <div
        className="relative h-full"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-grow h-full -mx-2">
          <InputTextAreaWrap
            key={`sms-textarea-${selectedLead?.id || "no-lead"}`}
            placeholderStyle={{
              color: buttonStyle()?.background === "#F59E0B" ? colorConfig.primaryDark : "",
            }}
            styling={buttonStyle()}
            className="relative resize-none focus-visible:outline-none border-none pr-[80px] pl-4 flex items-center rounded-[4px] pt-1 pb-5"
            id="text"
            ref={text}
            rows={1}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPaste={handlePaste}
            disabled={
              canDisabledConversation ||
              textDisabled() ||
              !selectedLead?.id ||
              messageLoader ||
              loading ||
              spinner ||
              leadsLoading ||
              loaderFn
            }
            onChange={e => {
              const newValue = e.target.value;
              if (newValue === "") {
                text.current.placeholder = lang.typeYourMessageHere;
                text.current.style.height = "0px";
                setisReview(false);
              }
              e.target.style.height = `${e.target.scrollHeight}px`;
              setmessageText(newValue);
            }}
            placeholder={placeholder()}
            name="text"
            maxLength={1000}
          />
        </div>
        {isDragging && !isUploadDisabled && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[4px] border-2 border-dashed border-brand-500 bg-brand-50/80 pointer-events-none">
            <p className="text-sm font-semibold text-brand-700">
              Drop images, videos, or documents to attach
            </p>
          </div>
        )}
        {mediaProcessing && (
          <div className="absolute bottom-1 left-2 text-xs text-grey-500 pointer-events-none">
            Processing file...
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if relevant props change (not when selectedLead.messages changes)
    return (
      prevProps.selectedLead?.id === nextProps.selectedLead?.id &&
      prevProps.messageText === nextProps.messageText &&
      prevProps.textDisabled === nextProps.textDisabled &&
      prevProps.messageLoader === nextProps.messageLoader &&
      prevProps.loading === nextProps.loading &&
      prevProps.spinner === nextProps.spinner &&
      prevProps.leadsLoading === nextProps.leadsLoading &&
      prevProps.loaderFn === nextProps.loaderFn &&
      prevProps.originalText === nextProps.originalText &&
      prevProps.setAttachments === nextProps.setAttachments
    );
  }
);
